import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { Ticket, TicketMessage } from '@/types/ticket';
import { ticketService } from '@/api/services/ticketService';
import { mapBackendResponseToTickets } from '@/utils/mappers/ticketMapper';
import { mentionsService } from '@/api/services/mentionsService';
import { getUserId } from '@/utils/helpers/helpers';

interface InboxState {
  tickets: Ticket[];
  ticketsByType: {
    all: Ticket[];
    assigned: Ticket[];
    unassigned: Ticket[];
    mentioned: Ticket[];
    bot: Ticket[];
  };
  selectedTicket: Ticket | null;
  loading: boolean;
  error: string | null;
  filters: {
    status: string | null;
    priority: string | null;
    assignee: string | null;
  };
  search: {
    query: string;
  };
}

const initialState: InboxState = {
  tickets: [],
  ticketsByType: {
    all: [],
    assigned: [],
    unassigned: [],
    mentioned: [],
    bot: []
  },
  selectedTicket: null,
  loading: false,
  error: null,
  filters: {
    status: null,
    priority: null,
    assignee: null
  },
  search: {
    query: ''
  }
};

export const fetchTickets = createAsyncThunk(
  'inbox/fetchTickets',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await ticketService.getTickets(params);
      return response;
    } catch (error) {
      console.error('📥 fetchTickets thunk failed:', error);
      return rejectWithValue('Failed to fetch tickets');
    }
  }
);

export const fetchTicketById = createAsyncThunk(
  'inbox/fetchTicketById',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      const response = await ticketService.getTicket(ticketId);
      return response;
    } catch (error) {
      return rejectWithValue(`Failed to fetch ticket ${ticketId}`);
    }
  }
);

export const fetchAssignedTickets = createAsyncThunk(
  'inbox/fetchAssignedTickets',
  async ({ userId, params = {} }: { userId: string; params?: { page?: number; limit?: number } }, { rejectWithValue }) => {
    try {
      const response = await ticketService.getAssignedTickets(getUserId() || '', params);
      return response;
    } catch (error) {
      console.error('📥 fetchAssignedTickets thunk failed:', error);
      return rejectWithValue('Failed to fetch assigned tickets');
    }
  }
);

export const fetchMentionedTickets = createAsyncThunk(
  'inbox/fetchMentionedTickets',
  async ({ userId, params = {} }: { userId: string; params?: { page?: number; limit?: number } }, { rejectWithValue }) => {
    try {
      const response = await ticketService.getMentionedTickets(userId, params);
      return response;
    } catch (error) {
      return rejectWithValue('Failed to fetch mentioned tickets');
    }
  }
);

export const fetchUnassignedTickets = createAsyncThunk(
  'inbox/fetchUnassignedTickets',
  async ({ userId, params = {} }: { userId: string; params?: { page?: number; limit?: number } }, { rejectWithValue }) => {
    try {
      const response = await ticketService.getUnassignedTickets(userId, params);
      return response;
    } catch (error) {
      console.error('📥 fetchUnassignedTickets thunk failed:', error);
      return rejectWithValue('Failed to fetch unassigned tickets');
    }
  }
);

export const fetchBotTickets = createAsyncThunk(
  'inbox/fetchBotTickets',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await ticketService.getBotTickets(params);
      return response;
    } catch (error) {
      console.error('📥 fetchBotTickets thunk failed:', error);
      return rejectWithValue('Failed to fetch bot tickets');
    }
  }
);

const inboxSlice = createSlice({
  name: 'inbox',
  initialState,
  reducers: {
    setTickets: (state, action: PayloadAction<Ticket[]>) => {
      state.tickets = action.payload;
      state.ticketsByType.all = action.payload;
    },
    setTicketsByType: (state, action: PayloadAction<{ type: 'all' | 'assigned' | 'unassigned' | 'mentioned' | 'bot', tickets: Ticket[] }>) => {
      const { type, tickets } = action.payload;
      state.ticketsByType[type] = tickets;
      if (type === 'all') {
        state.tickets = tickets;
      }
    },
    setNewResponseTicket: (state, action: PayloadAction<string>) => {
      const ticket = state.ticketsByType.all.find(t => t.id === action.payload);
      if (ticket) {
        ticket.isUnread = true;
        ticket.notificationType = 'new_response';
        ticket.hasNotification = true;
      }
      // update state with new ticket values
    },
    removeTicketNotification: (state, action: PayloadAction<string>) => {
      const ticket = state.ticketsByType.all.find(t => t.id === action.payload);
      if (ticket) {
        ticket.isUnread = false;
        ticket.notificationType = null;
        ticket.hasNotification = false;
      }
    },
    setSelectedTicket: (state, action: PayloadAction<Ticket>) => {
      // Preserve the messages if they exist on the current selected ticket
      const existingMessages = state.selectedTicket?.messages || [];
      const newMessages = action.payload.messages || [];

      // Use the messages from selected ticket if they're more than what's in the new payload
      const messages = existingMessages.length > newMessages.length ? existingMessages : newMessages;

      state.selectedTicket = {
        ...action.payload,
        messages
      };
    },
    updateSelectedTicketConversation: (state, action: PayloadAction<any>) => {
      const { ticketId, conversation } = action.payload;
      if (state.selectedTicket?.id === ticketId) {
        state.selectedTicket.conversation.push(conversation);
      }
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
    addTicketMessage: (state, action: PayloadAction<TicketMessage>) => {
      if (state.selectedTicket) {
        state.selectedTicket.messages.push(action.payload);
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilter: (state, action: PayloadAction<{ key: string; value: string | null }>) => {
      const { key, value } = action.payload;
      if (key in state.filters) {
        (state.filters as any)[key] = value;
      }
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.search.query = action.payload;
    },
    markTicketAsRead: (state, action: PayloadAction<string>) => {
      const ticket = state.tickets.find(t => t.id === action.payload);
      if (ticket) {
        ticket.isUnread = false;
      }

      Object.keys(state.ticketsByType).forEach(type => {
        const typedTicket = state.ticketsByType[type as keyof typeof state.ticketsByType].find(t => t.id === action.payload);
        if (typedTicket) {
          typedTicket.isUnread = false;
        }
      });
    },
    markTicketAsUnread: (state, action: PayloadAction<string>) => {
      const ticket = state.tickets.find(t => t.id === action.payload);
      if (ticket) {
        ticket.isUnread = true;
      }

      Object.keys(state.ticketsByType).forEach(type => {
        const typedTicket = state.ticketsByType[type as keyof typeof state.ticketsByType].find(t => t.id === action.payload);
        if (typedTicket) {
          typedTicket.isUnread = true;
        }
      });
    },
    updateTicket: (state, action: PayloadAction<Ticket>) => {
      const index = state.tickets.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.tickets[index] = action.payload;
      }

      Object.keys(state.ticketsByType).forEach(type => {
        const typedIndex = state.ticketsByType[type as keyof typeof state.ticketsByType].findIndex(t => t.id === action.payload.id);
        if (typedIndex !== -1) {
          state.ticketsByType[type as keyof typeof state.ticketsByType][typedIndex] = action.payload;
        }
      });

      if (state.selectedTicket?.id === action.payload.id) {
        state.selectedTicket = action.payload;
      }
    },
    clearTickets: (state, action: PayloadAction<'all' | 'assigned' | 'unassigned' | 'mentioned' | 'bot' | undefined>) => {
      const type = action.payload;
      if (type) {
        // Clear only the specified ticket list
        state.ticketsByType[type] = [];

        // Critical fix: Always set loading to true when clearing tickets
        // This ensures the view shows a loading state when switching tabs
        state.loading = true;

        // For backward compatibility, if 'all' is cleared, also clear main tickets array
        if (type === 'all') {
          state.tickets = [];
        }
      } else {
        // Clear all ticket lists
        state.tickets = [];
        state.ticketsByType = {
          all: [],
          assigned: [],
          unassigned: [],
          mentioned: [],
          bot: []
        };
        state.selectedTicket = null;
        state.loading = true;
      }
      state.error = null;
    },
    appendNewTicket: (state, action: PayloadAction<Ticket>) => {
      // dont push, insted concat to first position and check if the ticket is already in the array
      if (!state.ticketsByType.all.find(t => t.id === action.payload.id)) {
        state.ticketsByType.all = [action.payload, ...state.ticketsByType.all];
        state.tickets = [action.payload, ...state.tickets];
      } else {
        const index = state.ticketsByType.all.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.ticketsByType.all[index] = action.payload;
          state.tickets[index] = action.payload;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        const mappedTickets = mapBackendResponseToTickets(action.payload);
        state.tickets = mappedTickets;
        state.ticketsByType.all = mappedTickets;
        state.loading = false;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tickets = [];
        state.ticketsByType.all = [];
      })
      .addCase(fetchTicketById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.selectedTicket = action.payload;
        state.loading = false;
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAssignedTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignedTickets.fulfilled, (state, action) => {
        const mappedTickets = mapBackendResponseToTickets(action.payload);
        state.ticketsByType.assigned = mappedTickets;
        state.tickets = mappedTickets;
        state.loading = false;
      })
      .addCase(fetchAssignedTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tickets = [];
        state.ticketsByType.assigned = [];
      })
      .addCase(fetchMentionedTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMentionedTickets.fulfilled, (state, action) => {
        state.loading = false;
        try {
          const mentionsResponse = action.payload;
          if (!mentionsResponse.data) {
            state.tickets = [];
            state.ticketsByType.mentioned = [];
            return;
          }

          // Process each mention into a ticket format
          const ticketList = mentionsResponse.data.map((mention: any) => {
            return {
              id: mention.ticket?.id || mention.id,
              subject: mention.ticket?.title || 'New Mention',
              lastMessage: mention.content || '',
              assignee: null,
              customer: mention.ticket?.customer?.name || 'Unknown',
              customerId: mention.ticket?.customerId,
              company: mention.ticket?.company || '',
              status: mention.ticket?.status || 'open',
              priority: mention.ticket?.priority || 'medium',
              tags: [],
              createdAt: mention.mentionedAt || mention.createdAt || new Date().toISOString(),
              updatedAt: mention.updatedAt || mention.createdAt || new Date().toISOString(),
              isUnread: !mention.isRead,
              hasNotification: !mention.isRead,
              notificationType: 'mention' as 'mention' | 'update' | 'assignment' | 'new_response' | 'new_ticket',
              threadId: mention.ticketId,
              recipients: [],
              channel: null,
              language: 'en',
              type: null,
              customFields: {},
              team: mention.ticket?.team,
              teamId: mention.ticket?.teamId
            };
          });

          state.tickets = ticketList;
          state.ticketsByType.mentioned = ticketList;
        } catch (error) {
          console.error('Error processing mentions:', error);
          state.tickets = [];
          state.ticketsByType.mentioned = [];
        }
      })
      .addCase(fetchMentionedTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tickets = [];
        state.ticketsByType.mentioned = [];
      })
      .addCase(fetchUnassignedTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnassignedTickets.fulfilled, (state, action) => {
        const mappedTickets = mapBackendResponseToTickets(action.payload);
        state.ticketsByType.unassigned = mappedTickets;
        state.tickets = mappedTickets;
        state.loading = false;
      })
      .addCase(fetchUnassignedTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tickets = [];
        state.ticketsByType.unassigned = [];
      })
      .addCase(fetchBotTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBotTickets.fulfilled, (state, action) => {
        const mappedTickets = mapBackendResponseToTickets(action.payload);
        state.ticketsByType.bot = mappedTickets;
        state.tickets = mappedTickets;
        state.loading = false;
      })
      .addCase(fetchBotTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.tickets = [];
        state.ticketsByType.bot = [];
      });
  }
});

export const {
  setTickets,
  setTicketsByType,
  setSelectedTicket,
  clearSelectedTicket,
  setLoading,
  setError,
  setFilter,
  setSearchQuery,
  markTicketAsRead,
  markTicketAsUnread,
  updateTicket,
  addTicketMessage,
  clearTickets,
  updateSelectedTicketConversation,
  setNewResponseTicket,
  removeTicketNotification,
  appendNewTicket
} = inboxSlice.actions;

export const selectAllTickets = (state: RootState) => state.inbox.ticketsByType.all;
export const selectAssignedTickets = (state: RootState) => state.inbox.ticketsByType.assigned;
export const selectUnassignedTickets = (state: RootState) => state.inbox.ticketsByType.unassigned;
export const selectMentionedTickets = (state: RootState) => state.inbox.ticketsByType.mentioned;
export const selectBotTickets = (state: RootState) => state.inbox.ticketsByType.bot;

export const selectTickets = (state: RootState) => state.inbox.tickets;

export const selectSelectedTicket = (state: RootState) => state.inbox.selectedTicket;
export const selectInboxLoading = (state: RootState) => state.inbox.loading;
export const selectInboxError = (state: RootState) => state.inbox.error;
export const selectInboxFilters = (state: RootState) => state.inbox.filters;
export const selectInboxSearch = (state: RootState) => state.inbox.search;

export const selectAllLoading = (state: RootState) => {
  // Return true if tickets are being fetched or if the all tickets array is empty
  return state.inbox.loading;
};

export const selectAssignedLoading = (state: RootState) => {
  // Return true if tickets are being fetched or if there are no assigned tickets and we're in a loading state
  return state.inbox.loading;
};

export const selectUnassignedLoading = (state: RootState) => {
  // Return true if tickets are being fetched or if there are no unassigned tickets and we're in a loading state
  return state.inbox.loading;
};

export const selectMentionedLoading = (state: RootState) => {
  // Return true if tickets are being fetched or if there are no mentioned tickets and we're in a loading state
  return state.inbox.loading;
};

export const selectBotLoading = (state: RootState) => {
  return state.inbox.loading;
};

export default inboxSlice.reducer;
